import React from 'react';
import { intervalToDuration } from 'date-fns';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import { SurveyMigrationJob } from 'src/api/surveyMigration';
import { useDownloadSurveyMigrationErrors } from 'src/queries/useSurveyMigration';

interface ResultsSectionProps {
    job: SurveyMigrationJob;
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
    if (!startedAt) {
        return '—';
    }
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return '—';
    }
    const { days = 0, hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start, end });
    const totalHours = days * 24 + hours;
    const parts: string[] = [];
    if (totalHours > 0) parts.push(`${totalHours}h`);
    if (totalHours > 0 || minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
}

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}

function ResultsSection({ job }: ResultsSectionProps) {
    const downloadErrors = useDownloadSurveyMigrationErrors();

    const hasFailures = job.failedCount > 0;
    const isFailed = job.status === 'FAILED';

    let heading: string;
    if (isFailed) {
        heading = 'Migration Failed';
    } else if (hasFailures) {
        heading = 'Migration Completed with Errors';
    } else {
        heading = 'Migration Complete';
    }

    const handleDownload = async () => {
        let response;
        try {
            response = await downloadErrors.mutateAsync();
        } catch {
            // The mutation records the failure; downloadErrors.isError drives the UI error state.
            // Swallow here so the async onClick handler doesn't surface an unhandled rejection.
            return;
        }
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/csv' });
        const link = document.createElement('a');
        const objectUrl = window.URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = `survey-migration-errors-${job.migrationJobId}.csv`;
        link.click();
        // Defer revoke: revoking synchronously after click() can cancel the download before the
        // browser has started reading the blob.
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
    };

    return (
        <Card className={styles.cardWrap} label="Results">
            <Flex direction="column" className={styles.sectionBody}>
                <Flex direction="row" align="center" className={styles.resultHeading}>
                    {isFailed || hasFailures ? (
                        <Icon type="fa" name="exclamation-triangle" className={styles.resultWarningIcon} />
                    ) : (
                        <Icon type="fa" name="check-circle" className={styles.resultSuccessIcon} />
                    )}
                    <span className={styles.resultHeadingText}>{heading}</span>
                </Flex>

                <Flex direction="row" className={styles.progressStats}>
                    <div>
                        <span className={styles.metaLabel}>Migrated:</span> {job.processedCount.toLocaleString()}
                    </div>
                    <div>
                        <span className={styles.metaLabel}>Failed:</span> {job.failedCount.toLocaleString()}
                    </div>
                    <div>
                        <span className={styles.metaLabel}>Duration:</span>{' '}
                        {formatDuration(job.startedAt, job.completedAt)}
                    </div>
                </Flex>

                {downloadErrors.isError && (
                    <div className={styles.error}>
                        {errorMessage(downloadErrors.error, 'Failed to download the error report.')}
                    </div>
                )}

                {hasFailures && (
                    <Flex direction="row" className={styles.buttonRow}>
                        <Button
                            type="outline"
                            text="Download Error Report"
                            isLoading={downloadErrors.isLoading}
                            onClick={handleDownload}
                        />
                    </Flex>
                )}

                <div className={styles.nextSteps}>
                    Your survey data is now available in the new survey system. New surveys will automatically be
                    routed to the new system going forward.
                </div>
            </Flex>
        </Card>
    );
}

export default ResultsSection;
